import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 7: WeldWise Coach (MLOps RAG)
 * - Layer A: Ingestion + Processing
 * - Layer B: Indexing + KB Versioning + Eval Gate
 * - Layer C: Chatbot Runtime + Monitoring + Rollback
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 8,
    fontWeight: 800,
    color: "#0f172a",
    width: 260,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0b2e59",
    width: 160,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#4a0b2a",
    width: 160,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#0f3d1f",
    width: 170,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 8,
    fontWeight: 700,
    color: "#92400e",
    width: 220,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 10,
    fontWeight: 700,
    color: "#05333b",
    width: 150,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project7FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Layer backgrounds
      {
        id: "bg-ingest",
        position: { x: 40, y: 40 },
        data: { label: "" },
        style: {
          width: 1180,
          height: 260,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-index",
        position: { x: 40, y: 330 },
        data: { label: "" },
        style: {
          width: 1180,
          height: 240,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-runtime",
        position: { x: 40, y: 600 },
        data: { label: "" },
        style: {
          width: 1180,
          height: 280,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Layer titles
      { id: "sec-ingest", position: { x: 60, y: 50 }, data: { label: "Layer A — Ingestion + Processing" }, style: nodeStyle.section },
      { id: "sec-index", position: { x: 60, y: 340 }, data: { label: "Layer B — Indexing + KB Versioning" }, style: nodeStyle.section },
      { id: "sec-runtime", position: { x: 60, y: 610 }, data: { label: "Layer C — Runtime + Monitoring" }, style: nodeStyle.section },

      // Ingestion flow
      { id: "upload", position: { x: 90, y: 110 }, data: { label: "① S3 Raw Upload" }, style: nodeStyle.note },
      { id: "event", position: { x: 300, y: 110 }, data: { label: "② EventBridge" }, style: nodeStyle.net },
      { id: "sfn", position: { x: 500, y: 110 }, data: { label: "② Step Functions" }, style: nodeStyle.ops },
      { id: "router", position: { x: 720, y: 110 }, data: { label: "③ Lambda Router" }, style: nodeStyle.ops },

      // PDF path
      { id: "pdf", position: { x: 260, y: 190 }, data: { label: "④ EKS PDF Worker" }, style: nodeStyle.compute },
      { id: "pdf-tools", position: { x: 440, y: 190 }, data: { label: "Tika + Layout + Tables + OCR" }, style: { ...nodeStyle.note, width: 260 } },
      { id: "pdf-norm", position: { x: 720, y: 180 }, data: { label: "⑦ PDF Normalize JSON" }, style: nodeStyle.data },
      { id: "pdf-chunk", position: { x: 960, y: 190 }, data: { label: "⑧ Chunking" }, style: nodeStyle.data },

      // Media path
      { id: "media", position: { x: 260, y: 250 }, data: { label: "⑨ EKS Media Worker" }, style: nodeStyle.compute },
      { id: "media-tools", position: { x: 440, y: 250 }, data: { label: "FFmpeg + Whisper + PyAnnote" }, style: { ...nodeStyle.note, width: 220 } },
      { id: "media-norm", position: { x: 720, y: 250 }, data: { label: "⑬ Transcript Normalize" }, style: nodeStyle.data },
      { id: "media-chunk", position: { x: 960, y: 250 }, data: { label: "⑭ Chunking" }, style: nodeStyle.data },

      // Indexing + KB
      { id: "embed", position: { x: 120, y: 410 }, data: { label: "⑮ EKS Embeddings" }, style: nodeStyle.compute },
      { id: "faiss", position: { x: 320, y: 410 }, data: { label: "⑯ FAISS Index" }, style: nodeStyle.compute },
      { id: "meta", position: { x: 520, y: 410 }, data: { label: "⑰ Metadata Store" }, style: nodeStyle.data },
      { id: "kb", position: { x: 720, y: 410 }, data: { label: "⑱ KB Versioning" }, style: nodeStyle.ops },
      { id: "eval", position: { x: 940, y: 410 }, data: { label: "⑲–⑳ Eval Suite" }, style: nodeStyle.ops },
      { id: "gate", position: { x: 940, y: 470 }, data: { label: "㉑ Quality Gate" }, style: nodeStyle.note },

      // Runtime
      { id: "ui", position: { x: 120, y: 690 }, data: { label: "㉒ React UI" }, style: nodeStyle.compute },
      { id: "api", position: { x: 320, y: 690 }, data: { label: "㉓ API Gateway + Chat Service" }, style: { ...nodeStyle.ops, width: 220 } },
      { id: "pre", position: { x: 580, y: 690 }, data: { label: "㉔ Query Preprocess" }, style: nodeStyle.ops },
      { id: "retrieval", position: { x: 780, y: 690 }, data: { label: "㉕ Retrieval + Rerank" }, style: nodeStyle.compute },
      { id: "llm", position: { x: 980, y: 690 }, data: { label: "㉖ LLM + Citations" }, style: nodeStyle.compute },

      { id: "logs", position: { x: 320, y: 780 }, data: { label: "㉗ Logs + Feedback" }, style: nodeStyle.data },
      { id: "obs", position: { x: 580, y: 780 }, data: { label: "㉘ CloudWatch Dashboards" }, style: nodeStyle.ops },
      { id: "drift", position: { x: 840, y: 780 }, data: { label: "㉙ Drift/Gaps" }, style: nodeStyle.ops },
      { id: "reindex", position: { x: 1060, y: 780 }, data: { label: "㉚ Re-index" }, style: nodeStyle.ops },
      { id: "rollback", position: { x: 980, y: 830 }, data: { label: "㉛ Rollback" }, style: nodeStyle.note },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const orange = { stroke: "#f59e0b" };
    const purple = { stroke: "#7c3aed" };

    return [
      mkEdge("e1", "upload", "event", "①→②", { style: { ...blue } }),
      mkEdge("e2", "event", "sfn", "②", { style: { ...blue } }),
      mkEdge("e3", "sfn", "router", "③", { style: { ...blue } }),

      mkEdge("e4", "router", "pdf", "④", { style: { ...blue } }),
      mkEdge("e4b", "pdf", "pdf-tools", "Process", { style: { ...blue } }),
      mkEdge("e5", "pdf-tools", "pdf-norm", "⑦", { style: { ...blue } }),
      mkEdge("e6", "pdf-norm", "pdf-chunk", "⑧", { style: { ...blue } }),

      mkEdge("e7", "router", "media", "⑨", { style: { ...blue } }),
      mkEdge("e7b", "media", "media-tools", "⑩–⑫", { style: { ...blue } }),
      mkEdge("e8", "media-tools", "media-norm", "⑬", { style: { ...blue } }),
      mkEdge("e9", "media-norm", "media-chunk", "⑭", { style: { ...blue } }),

      mkEdge("e10", "pdf-chunk", "embed", "⑮", { style: { ...green } }),
      mkEdge("e11", "media-chunk", "embed", "⑮", { style: { ...green } }),
      mkEdge("e12", "embed", "faiss", "⑯", { style: { ...green } }),
      mkEdge("e13", "embed", "meta", "⑰", { style: { ...green } }),
      mkEdge("e14", "faiss", "kb", "⑱", { style: { ...green } }),
      mkEdge("e15", "kb", "eval", "⑲–⑳", { style: { ...green } }),
      mkEdge("e16", "eval", "gate", "㉑", { style: { ...green } }),

      mkEdge("e17", "ui", "api", "㉒→㉓", { style: { ...orange } }),
      mkEdge("e18", "api", "pre", "㉔", { style: { ...orange } }),
      mkEdge("e19", "pre", "retrieval", "㉕", { style: { ...orange } }),
      mkEdge("e20", "retrieval", "llm", "㉖", { style: { ...orange } }),
      mkEdge("e21", "llm", "logs", "㉗", { style: { ...orange } }),
      mkEdge("e22", "logs", "obs", "㉘", { style: { ...purple } }),
      mkEdge("e23", "obs", "drift", "㉙", { style: { ...purple } }),
      mkEdge("e24", "drift", "reindex", "㉚", { style: { ...purple } }),
      mkEdge("e25", "reindex", "rollback", "㉛", { style: { ...purple } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "980px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.4}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
